/**
 * Storage Management Utility
 *
 * Handles selective clearing of localStorage and sessionStorage
 * Preserves user preferences while clearing temporary state
 */

/**
 * Storage keys that should be preserved across version updates
 *
 * These keys contain user preferences and critical configuration
 * that should not be cleared when the app version changes
 */
const PRESERVED_KEYS = [
  'chariot-app-version', // Version tracking key
  'aws-marketplace-config', // AWS marketplace configuration
  'aws-marketplace-config-verify-linking', // AWS marketplace verification state
  'theme', // User theme preference
  'data-theme', // Alternative theme storage key
  'lastVisitedUser', // User preference for last visited user
] as const;

/**
 * Check if a storage key should be preserved
 *
 * Keys starting with certain prefixes or matching preserved keys
 * should not be cleared during version updates
 *
 * @param key - localStorage/sessionStorage key to check
 * @returns True if key should be preserved
 */
function shouldPreserveKey(key: string): boolean {
  // Check exact matches
  if (PRESERVED_KEYS.includes(key as (typeof PRESERVED_KEYS)[number])) {
    return true;
  }

  // Check AWS Amplify keys (authentication tokens)
  // These keys start with 'CognitoIdentityServiceProvider.'
  if (key.startsWith('CognitoIdentityServiceProvider.')) {
    return true;
  }

  // Check AWS-related keys
  if (key.startsWith('aws-')) {
    return true;
  }

  return false;
}

/**
 * Selectively clear localStorage on version change
 *
 * Preserves user preferences and authentication state
 * while clearing temporary data and cache
 */
export function clearStorageOnVersionChange(): void {
  try {
    // Save items we want to preserve from localStorage
    const itemsToPreserve: Record<string, string> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && shouldPreserveKey(key)) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          itemsToPreserve[key] = value;
        }
      }
    }

    // Clear all localStorage
    localStorage.clear();

    // Restore preserved items
    Object.entries(itemsToPreserve).forEach(([key, value]) => {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.error(`[Storage] Failed to restore key: ${key}`, error);
      }
    });
  } catch (error) {
    console.error(
      '[Storage] Failed to clear storage on version change:',
      error
    );
  }
}

/**
 * Clear all storage (localStorage and sessionStorage)
 *
 * Used for complete logout or keychain login scenarios
 * Does not preserve any keys - full reset
 */
export function clearAllStorage(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    console.error('[Storage] Failed to clear all storage:', error);
  }
}

/**
 * Check if localStorage is available
 *
 * Some browsers/environments may have localStorage disabled
 * or throw errors when accessing it
 *
 * @returns True if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__chariot_storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
